import { Box, IconButton, Tooltip } from "@mui/material";
import AttachIcon from "@mui/icons-material/AttachFile";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { Airframe } from "../../../../types/airframe";
import { useHasPermission } from "../../../../hooks/useHasPermission";
import { MINI_PACK_ICON_TOOLTIP } from "../../../../utils/miniPackExportJobUtils";

type Props = {
  pkg: Airframe;
  onAttach: (pkg: Airframe) => void;
  onEdit: (pkg: Airframe) => void;
  onDelete: (pkg: Airframe) => void;
  onMiniPack: (pkg: Airframe) => void;
  stopPropagation?: boolean;
  inline?: boolean;
};

export function AirframeActionButtons({
  pkg,
  onAttach,
  onEdit,
  onDelete,
  onMiniPack,
  stopPropagation = false,
  inline = false,
}: Props) {
  const canUpdateAirframe = useHasPermission("assets-airframes", "update");
  const canDeleteAirframe = useHasPermission("assets-airframes", "delete");
  const canAttachComponent = useHasPermission("assets-airframes", "create");
  const canAccessMiniPackExport = useHasPermission("assets-mini-pack-export", "read");

  const handleClick =
    (handler: () => void) => (e: React.MouseEvent) => {
      if (stopPropagation) {
        e.stopPropagation();
      }
      handler();
    };

  return (
    <Box
      sx={
        inline
          ? {
              display: "inline-flex",
              flexWrap: "nowrap",
              alignItems: "center",
              flexShrink: 0,
              whiteSpace: "nowrap",
              "& .MuiIconButton-root": { mr: 0 },
            }
          : undefined
      }
    >
      {canAttachComponent && (
        <IconButton
          size="small"
          sx={{ color: "hsl(19 101% 51% / 1)", mr: inline ? 0 : 1 }}
          onClick={handleClick(() => onAttach(pkg))}
        >
          <AttachIcon />
        </IconButton>
      )}

      {canUpdateAirframe && canAccessMiniPackExport && (
        <Tooltip title={MINI_PACK_ICON_TOOLTIP}>
          <IconButton
            size="small"
            sx={{ color: "hsl(19 101% 51% / 1)", mr: inline ? 0 : 1 }}
            onClick={handleClick(() => onMiniPack(pkg))}
            aria-label="Configure mini-pack PDF"
          >
            <PictureAsPdfIcon />
          </IconButton>
        </Tooltip>
      )}

      {canUpdateAirframe && (
        <IconButton
          size="small"
          sx={{ color: "hsl(19 101% 51% / 1)", mr: inline ? 0 : 1 }}
          onClick={handleClick(() => onEdit(pkg))}
        >
          <EditIcon />
        </IconButton>
      )}

      {canDeleteAirframe && (
        <IconButton
          size="small"
          sx={{ color: "#f44336" }}
          onClick={handleClick(() => onDelete(pkg))}
        >
          <DeleteIcon />
        </IconButton>
      )}
    </Box>
  );
}
